import { useEffect, useState } from "react";
import { isEmpty } from "lodash";

import getUserProfileAPI from "../api/getUserProfileAPI";
import { getToken, getUserData } from "../common";

export const AUCTION_CREATE_DENIED_MESSAGE =
  "Auction creation rights are not enabled for your account. Please contact the administrator to get access.";

// auction creation rights are granted per seller by an admin (partners.auctionStatus),
// and the profile API exposes them as `canCreateAuction`.
export const resolveCanCreateAuction = (profile, role) => {
  if (typeof profile?.canCreateAuction === "boolean") {
    return profile.canCreateAuction;
  }

  // fallback for API responses that only carry the raw partner flag
  return role === "SELLER" && !isEmpty(profile) && profile.auctionStatus !== "NO";
};

/**
 * Tells whether the signed in account is allowed to create an auction, so the
 * UI can hide/disable the entry points instead of letting the user fill in the
 * whole auction form and only find out on submit.
 */
const useAuctionCreateAccess = () => {
  const [access, setAccess] = useState({
    isLoading: true,
    canCreateAuction: false,
  });

  useEffect(() => {
    let isSubscribed = true;

    const updateAccess = (canCreateAuction) => {
      if (isSubscribed) setAccess({ isLoading: false, canCreateAuction });
    };

    const fetchAccess = async () => {
      const userData = getUserData() ?? {};

      // only sellers own auctions - nothing to look up for anyone else
      if (!getToken() || userData.role !== "SELLER") {
        updateAccess(false);
        return;
      }

      try {
        const { data } = await getUserProfileAPI();

        updateAccess(resolveCanCreateAuction(data, userData.role));
      } catch (error) {
        console.error(error);

        // the profile API is the source of truth, but a transient failure should
        // not lock a seller out of a flow the backend still guards on submit
        updateAccess(true);
      }
    };

    fetchAccess();

    return () => {
      isSubscribed = false;
    };
  }, []);

  return access;
};

export default useAuctionCreateAccess;
